import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * Shared "How the three layers connect" explainer for the Intent subsystem.
 *
 * Renders the canonical flow:
 *   Template  →  Bind  →  Enforce  →  Seal
 *
 * Used in two surfaces so the mental model is identical wherever the operator
 * is in the journey:
 *   - IntentAuthoringConsole (left-nav catalog) — authoring a Template.
 *   - IntentBindingsPanel (agent Intent step) — Bind & Materialize, the
 *     explicit NEXT STEP after a template version is approved.
 *
 * Pass {@code active} to highlight the layer the current surface owns
 * ('template' | 'bind' | 'enforce' | 'seal'). The active cell gets a ring so
 * the operator sees "you are here" in the four-stage pipeline.
 */
export function IntentLayersExplainer({ active = null, title = 'How the three layers connect' }) {
  const cells = [
    { key: 'template', title: 'Template', body: 'The allowed tools and rules — once approved, locked.',
      accent: { bg: 'var(--v2-accent-cyan-soft)', fg: 'var(--v2-accent-cyan)' } },
    { key: 'bind', title: 'Bind', body: 'Attach an approved version to this agent.',
      accent: null },
    { key: 'enforce', title: 'Enforce', body: 'Every request is checked against it in real time.',
      accent: null },
    { key: 'seal', title: 'Seal', body: 'Each decision is recorded as tamper-evident proof.',
      accent: { bg: 'var(--v2-success-soft)', fg: 'var(--v2-success)' } },
  ];

  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--v2-text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
      }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
        {cells.map((c, i) => (
          <React.Fragment key={c.key}>
            <div style={{
              flex: 1, minWidth: 0,
              background: c.accent ? c.accent.bg : 'var(--v2-bg-muted)',
              borderRadius: 6, padding: 10,
              outline: active === c.key ? '2px solid var(--v2-accent-cyan)' : 'none',
              outlineOffset: active === c.key ? '1px' : 0,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: c.accent ? c.accent.fg : 'var(--v2-text-primary)' }}>
                {c.title}
              </div>
              <div style={{ fontSize: 11, color: c.accent ? c.accent.fg : 'var(--v2-text-secondary)', marginTop: 3, lineHeight: 1.4 }}>
                {c.body}
              </div>
            </div>
            {i < cells.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'var(--v2-text-muted)' }}>
                <ChevronRight size={14} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default IntentLayersExplainer;
